import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, useLogin } from '@encarpkg/design';
import { Enlog } from '@encarpkg/shared';
import dayjs from 'app/libs/dayjs';
import { numberWithCommas } from 'app/libs/number';
import useCars from 'app/hook/cars/useCars';
import useAccordion from 'app/hook/common/useAccordion';
import Table from 'app/components/common/Table';
import ReportAccidentInfoItem from 'app/components/cars/ReportAccidentInfoItem';
import useAuth from 'app/hook/common/useAuth';
import useExportCarReportAccident from 'app/hook/export/useExportCarReportAccident';
import classNames from 'classnames/bind';
import styles from './ReportAccidentInfo.module.scss';

const cx = classNames.bind(styles);

function ReportAccidentInfo() {
	const { base, accident } = useCars();
	const { setAccordionLength, accordionArray, handleAccordionMulti, accordionStatsAll, handleAccordionAll } = useAccordion();
	const { isLoggedIn } = useAuth();
	const { goLogin } = useLogin();
	const { isFromExportDetail } = useExportCarReportAccident();

	const [loginPass, setLoginPass] = useState(null);

	useEffect(() => {
		if (isFromExportDetail) {
			setLoginPass(true);
			return;
		}
		if (isLoggedIn !== null) {
			setLoginPass(Boolean(isLoggedIn));
		}
	}, [isFromExportDetail, isLoggedIn]);

	const baseVehicleNo = base.vehicleNo;
	const isChangUseHistory = accident.loan || accident.business || accident.government;
	const itemData = useMemo(
		() => [
			{ id: 0, title: '자동차 일반 사양 정보' },
			{ id: 1, title: <>자동차 용도 이력 정보{!!isChangUseHistory && <span className={cx('append_title')}> (있음)</span>}</> },
			{ id: 2, title: '자동차 번호 / 소유자 변경 이력 정보' },
			{ id: 3, title: '자동차보험 특수 사고이력 정보' },
			{ id: 4, title: '보험사고 이력 정보 : 내차 피해' },
			{ id: 5, title: '보험사고 이력 정보 : 타차 가해' },
			{ id: 6, title: '정보이용관련 유의사항' },
		],
		[isChangUseHistory],
	);
	const carInfoUse1sData = [
		{ id: '1', use: '관용' },
		{ id: '2', use: '자가용' },
		{ id: '3', use: '영업용(일반)' },
		{ id: '4', use: '개인택시' },
		{ id: '5', use: '영업용(대여)' },
	];
	const carInfoUse2sData = [
		{ id: '1', use: '승용' },
		{ id: '2', use: '승합' },
		{ id: '3', use: '화물' },
		{ id: '4', use: '특수' },
	];
	const carUseText = useCallback((param, data) => {
		if (!param) return false;
		const value = data
			.filter((item) => param.includes(item.id))
			.map((item) => (
				<span
					key={item.id}
					className={cx('txt')}
				>
					{item.use}
				</span>
			));
		return value;
	}, []);
	const use1 = accident.carInfoUse1s;
	const use2 = accident.carInfoUse2s;
	const carInfoUse1sValue = carUseText(accident.use, carInfoUse1sData);
	const carInfoUse2sValue = carUseText(accident.carKind, carInfoUse2sData);

	// 자동차 번호, 소유자 변경 이력 정보
	const changeHistorys = accident.ownerChanges
		?.reduce((acc, cur) => {
			acc.push({ title: '소유자변경', date: dayjs(cur).format('YYYY/MM/DD') });
			return acc;
		}, [])
		.concat(
			accident.carInfoChanges.reduce((acc, cur, index) => {
				let titleText = '차량번호 변경';
				if (index === 0) titleText = '최초 차량번호';
				acc.push({
					...cur,
					title: titleText,
					use: [carUseText(use1[index], carInfoUse1sData), carUseText(use2[index], carInfoUse2sData)],
					date: dayjs(cur.date).format('YYYY/MM/DD'),
				});
				return acc;
			}, []),
		)
		.sort((a, b) => dayjs(b.date, 'YYYY/MM/DD') - dayjs(a.date, 'YYYY/MM/DD'));
	const changeHistorysItem = changeHistorys.map((item, index) => {
		const key = `key${index}`;
		return (
			<tr key={key}>
				<th>{item.date}</th>
				<td>{item.title}</td>
				<td>{item.carNo || ''}</td>
				<td>
					{item.use ? (
						<>
							{item.use[0]} {item.use[1]}
						</>
					) : (
						''
					)}
				</td>
			</tr>
		);
	});

	// 보험사고 이력 정보
	const isDamageMyCar = accident.accidents.filter((item) => item.type === '1').length > 0;
	const isAccidentMyCar = accident.accidents.filter((item) => item.type === '2').length > 0;
	const isAccidentOtherCar = accident.accidents.filter((item) => item.type === '3').length > 0;
	const damageCarTable = useCallback(
		(param) =>
			accident.accidents
				.filter((item) => item.type === param)
				.map((item, index) => {
					const key = `key${index}`;
					const totalPrice = item.partCost + item.laborCost + item.paintingCost;
					let benefit;
					if (totalPrice === 0) {
						benefit = item.insuranceBenefit > 0 ? `${numberWithCommas(item.insuranceBenefit)}원` : '미확정';
					} else {
						benefit = `${numberWithCommas(totalPrice)}원`;
					}
					return (
						<Table
							key={key}
							type="row"
							tableAlign="left"
							style={{ marginTop: '15px' }}
						>
							<colgroup>
								<col style={{ width: '35px' }} />
								<col style={{ width: '80px' }} />
								<col style={{ width: '35px' }} />
								<col style={{ width: '80px' }} />
								<col style={{ width: '35px' }} />
								<col style={{ width: '80px' }} />
							</colgroup>
							<tbody>
								<tr>
									<th colSpan="6">
										<span className={cx('date')}>사고일자: {item.date}</span>
										<em className={cx('price')}>수리비용: {benefit}</em>
									</th>
								</tr>
								<tr className={cx('row_price')}>
									<th>부품</th>
									<td>{numberWithCommas(item.partCost)}원</td>
									<th>공임</th>
									<td>{numberWithCommas(item.laborCost)}원</td>
									<th>도장</th>
									<td>{numberWithCommas(item.paintingCost)}원</td>
								</tr>
							</tbody>
						</Table>
					);
				}),
		[accident.accidents],
	);
	const notJoinDateValue = useCallback((param) => {
		const value = param.split(/[^0-9]+/g);
		const start = `${value[0].substring(0, 4)}년 ${value[0].substring(4, 6)}월`;
		const end = `${value[1].substring(0, 4)}년 ${value[1].substring(4, 6)}월`;
		return `${start} ~ ${end}`;
	}, []);

	useEffect(() => {
		setAccordionLength(itemData.length);
	}, [setAccordionLength, itemData]);

	// 로그인 영역
	const loginNext = () => {
		setLoginPass(true);
	};
	const handleLogin = useCallback(
		(e) => {
			e.preventDefault();
			const returnUrl = window.location.href;
			goLogin({ returnUrl });
		},
		[goLogin],
	);
	useEffect(() => {
		if (loginPass === null || loginPass) return;

		const dataTalkViewSingle = { eventnamegroup: '로그인유도' };
		Enlog().enloggingJsonData({
			dataTalkViewSingle,
		});
	}, [loginPass]);

	return (
		<>
			<div className={cx('info_accident')}>
				<strong className={cx('tit_info')}>상세 정보</strong>
				<button
					className={cx('btn_showall')}
					onClick={() => loginPass && handleAccordionAll()}
					disabled={!loginPass}
				>
					{accordionStatsAll ? '모두 펼쳐보기' : '모두 닫기'}
				</button>
				<div className={cx('login_area', { on: !loginPass, from_export: isFromExportDetail })}>
					<p className={cx('text')}>
						로그인 후 {accident.carNo} 차량의
						<br />
						상세한 보험이력을 확인해 보세요
					</p>

					<div className={cx('btn_area')}>
						<div className={cx('btn_area_item')}>
							<Button.V2Renew
								size={44}
								color="white"
								onClick={() => loginNext()}
								data-enlog-dt-eventnamegroup="로그인유도"
							>
								다음에
							</Button.V2Renew>
						</div>
						<div className={cx('btn_area_item')}>
							<Button.V2Renew
								size={44}
								onClick={handleLogin}
								data-enlog-dt-eventnamegroup="로그인유도"
								data-enlog-dt-hit="detail_accident_report_login_cta"
							>
								로그인하기
							</Button.V2Renew>
						</div>
					</div>
				</div>
				<ReportAccidentInfoItem
					accordionArray={accordionArray}
					handleAccordionMulti={handleAccordionMulti}
					accordionStatsAll={accordionStatsAll}
					itemData={itemData[0]}
				>
					{loginPass && (
						<>
							<Table
								type="row"
								tableAlign="left"
							>
								<colgroup>
									<col style={{ width: '70px' }} />
									<col />
									<col style={{ width: '80px' }} />
									<col />
								</colgroup>
								<tbody>
									<tr>
										<th>제작사</th>
										<td>{accident.maker}</td>
										<th>자동차명</th>
										<td>{base.category?.modelName}</td>
									</tr>
									<tr>
										<th>연식</th>
										<td>{accident.year}년</td>
										<th>사용연료</th>
										<td>{accident.fuel}</td>
									</tr>
									<tr>
										<th>배기량</th>
										<td>{accident.displacement}cc</td>
										<th>차체형상</th>
										<td>{accident.carShape}</td>
									</tr>
									<tr>
										<th>
											용도 및<br />
											차종
										</th>
										<td>
											{carInfoUse1sValue}
											{carInfoUse2sValue}
										</td>
										<th>
											최초 보험
											<br />
											가입일자
										</th>
										<td>{dayjs(accident.firstDate).format('YYYY/MM/DD')}</td>
									</tr>
								</tbody>
							</Table>
							<p className={cx('desc')}>
								본 정보는 자동차 일반 정보로서 {accident.carNo} 차량을 확인하기 위하여 참고로 제공하는 것이며, 수입차나 특수차량 등의
								경우에는 일부 정보의 누락이나 오류가 있을 수 있습니다.
							</p>
						</>
					)}
				</ReportAccidentInfoItem>
				<ReportAccidentInfoItem
					accordionArray={accordionArray}
					handleAccordionMulti={handleAccordionMulti}
					accordionStatsAll={accordionStatsAll}
					itemData={itemData[1]}
				>
					{loginPass && (
						<>
							<Table
								type="row"
								tableAlign="left"
							>
								<colgroup>
									<col style={{ width: '152px' }} />
									<col />
								</colgroup>
								<tbody>
									<tr>
										<th>영업용(대여) 사용이력</th>
										<td>사용이력 {accident.loan ? '있음' : '없음'}</td>
									</tr>
									<tr>
										<th>영업용(일반) 사용이력</th>
										<td>사용이력 {accident.business ? '있음' : '없음'}</td>
									</tr>
									<tr>
										<th>관용용도 사용이력</th>
										<td>사용이력 {accident.government ? '있음' : '없음'}</td>
									</tr>
								</tbody>
							</Table>
							<p className={cx('desc')}>
								본 정보는 {accident.carNo} 차량의 과거 자동차번호 변경기록을 모두 검색하여 제공하는 것으로 영업용(대여) 이력(렌트카),
								영업용(일반) 이력(택시 등)으로 사용된 적이 있는지를 확인할 수 있습니다.
							</p>
						</>
					)}
				</ReportAccidentInfoItem>
				<ReportAccidentInfoItem
					accordionArray={accordionArray}
					handleAccordionMulti={handleAccordionMulti}
					accordionStatsAll={accordionStatsAll}
					itemData={itemData[2]}
				>
					{loginPass && (
						<Table
							type="row"
							tableAlign="left"
						>
							<colgroup>
								<col style={{ width: '80px' }} />
								<col />
								<col />
								<col />
							</colgroup>
							<tbody>{changeHistorysItem}</tbody>
						</Table>
					)}
				</ReportAccidentInfoItem>
				{loginPass && (
					<>
						<ReportAccidentInfoItem
							accordionArray={accordionArray}
							handleAccordionMulti={handleAccordionMulti}
							accordionStatsAll={accordionStatsAll}
							itemData={itemData[3]}
						>
							<Table
								type="row"
								tableAlign="left"
							>
								<colgroup>
									<col style={{ width: '104px' }} />
									<col />
								</colgroup>
								<tbody>
									<tr>
										<th>전손 보험사고</th>
										<td>{!accident.totalLossCnt ? '이력없음' : '이력있음'}</td>
									</tr>
									<tr>
										<th>침수 보험사고</th>
										<td>침수(전손,분손) {accident.floodTotalLossCnt || 0}회</td>
									</tr>
									<tr>
										<th>도난 보험사고</th>
										<td>{!accident.robberCnt ? '이력없음' : '이력있음'}</td>
									</tr>
								</tbody>
							</Table>
							<p className={cx('desc')}>
								자동차보험으로 처리된 사고기록 중에서 전손사고, 도난사고, 침수사고 발생 여부를 확인할 수 있으며, 침수사고는 부분침수로
								인한 경미한 사고일 수도 있습니다.
							</p>
						</ReportAccidentInfoItem>
						<ReportAccidentInfoItem
							accordionArray={accordionArray}
							handleAccordionMulti={handleAccordionMulti}
							accordionStatsAll={accordionStatsAll}
							itemData={itemData[4]}
						>
							<p className={cx('desc')}>
								자동차사고로 발생한 {accident.carNo}차량의 손상&middot;수리기록을 수리비(보험금)출처에 따라 &apos;
								<em className={cx('emph')}>① 자기 자동차보험에서 지급된 경우</em>&apos;와 &apos;
								<em className={cx('emph')}>② 타인의 자동차보험에서 지급된 경우</em>&apos;로나누어 제공합니다. (대인사고는 제외합니다)
							</p>
							<strong className={cx('tit')}>① 내차보험으로 처리한 내차사고</strong>
							<p className={cx('desc')}>자동차 사고로 {accident.carNo} 차량이 입은 손해를 동 차량의 보험으로 처리한 내역입니다.</p>
							{!isDamageMyCar && <p className={cx('box_nodata')}>사고 내역 없음</p>}
							{isDamageMyCar && <>{damageCarTable('1')}</>}
							{(accident.notJoinDate1 ||
								accident.notJoinDate2 ||
								accident.notJoinDate3 ||
								accident.notJoinDate4 ||
								accident.notJoinDate5) && (
								<dl className={cx('date_join')}>
									<dt>정보제공 불가능기간</dt>
									{accident.notJoinDate1 && <dd>{notJoinDateValue(accident.notJoinDate1)}</dd>}
									{accident.notJoinDate2 && <dd>{notJoinDateValue(accident.notJoinDate2)}</dd>}
									{accident.notJoinDate3 && <dd>{notJoinDateValue(accident.notJoinDate3)}</dd>}
									{accident.notJoinDate4 && <dd>{notJoinDateValue(accident.notJoinDate4)}</dd>}
									{accident.notJoinDate5 && <dd>{notJoinDateValue(accident.notJoinDate5)}</dd>}
								</dl>
							)}
							<p className={cx('desc')}>
								자기차량손해담보 미가입기간으로서 자기차량손해담보에 의해 지급된 자동차수리비 정보를 제공할 수 없습니다.
							</p>
							<strong className={cx('tit')}>② 타차보험에서 처리된 내차 사고</strong>
							<p className={cx('desc')}>
								자동차 사고로 {baseVehicleNo} 차량이 입은 손해가 다른 차량의 자동차 보험에서 처리된 내역입니다.
							</p>
							{!isAccidentMyCar && <p className={cx('box_nodata')}>사고 내역 없음</p>}
							{isAccidentMyCar && damageCarTable('2')}
							<p className={cx('desc')}>
								쌍방과실로 해당 자동차의 손상&middot;수리기록이 동 차량의 자동차 보험과 타차 자동차 보험에서 동시에 처리된 경우에는,
								&apos;①내차 보험으로 처리한 내차 사고&apos;에만 표시되고 &apos;②타차보험에서 처리된 내차 사고&apos;에서는 생략됩니다.
							</p>
						</ReportAccidentInfoItem>
						<ReportAccidentInfoItem
							accordionArray={accordionArray}
							handleAccordionMulti={handleAccordionMulti}
							accordionStatsAll={accordionStatsAll}
							itemData={itemData[5]}
						>
							<p className={cx('desc')}>
								{baseVehicleNo} 차량이 타인의 자동차등의 재물에 피해를 입혀, 그 손해를 해당 차량의 자동차 보험으로 처리한
								내역입니다.(대인사고는 제외합니다)
							</p>
							{!isAccidentOtherCar && <p className={cx('box_nodata')}>사고 내역 없음</p>}
							{isAccidentOtherCar && damageCarTable('3')}
							<p className={cx('desc')}>
								본 정보는 자동차사고로 상대방 차량 또는 재물에 발생한 손해와 관련된 정보로서, 상대방 차량 또는 재물에 발생한 손해
								규모를 참고하여 자기 자동차의 손상여부 등을 간접적으로 확인할 수 있도록 제공하여 드리고 있습니다.
							</p>
						</ReportAccidentInfoItem>
						<ReportAccidentInfoItem
							accordionArray={accordionArray}
							handleAccordionMulti={handleAccordionMulti}
							accordionStatsAll={accordionStatsAll}
							itemData={itemData[6]}
						>
							<strong className={cx('notice')}>
								자동차보험사고기록이 없었다고 해서
								<br />
								반드시 무사고라고 할 수는 없습니다.
							</strong>
							<p className={cx('desc')}>
								중고차 사고이력정보 서비스는 자동차 보험을 취급하는 14개 손해보험사의 자동차보험수리비 지급기록(1996년 이후)에
								근거하여 제공하고 있습니다. 따라서 다음과 같은 경우에는 중고차 사고 이력정보 서비스를 제공할 수 없습니다.
								<br />
								<br />
								1)사고가 있었다하더라도 보험회사에 사고신고를 하지 않고 자비로 처리한 경우, 사고신고를 하였더라도 면책, 취소 등의
								사유로 보험금이 지급되지 않은 경우, 사고신고 후 자비로 처리한 경우입니다.
								<br />
								<br />
								2)사고가 있었더라도 종합보험, 즉 자기차량담보나 대물배상담보의 자동차보험에 가입하지 않아서 자동차보험으로 수리비용을
								청구하지 못한 경우(단, 이 경우라도 타인 자동차보험에서 보상받은 경우에는 사고정보 제공 가능)
								<br />
								<br />
								3)자동차보험이 아닌 운수공제(택시공제, 화물공제,버스공제)에 가입되어 운수공제로부터 자동차의 피해에 대한 손해를
								보상받은 경우 등<br />
								<br />본 중고차 사고이력 정보는 중고차 품질확인을 위한 보조정보이며 결정적인 판단자료로 사용되어서는 아니됩니다.
								따라서 정밀한 중고차 품질확인을 원하시면 차량진단 전문업체의 진단을 받아보시기 바랍니다.
							</p>
						</ReportAccidentInfoItem>
					</>
				)}
			</div>
			<div className={cx('info_provider')}>
				<p className={cx('desc')}>
					보험개발원(www.kidi.or.kr)은 보험업법 제176조에 의하여 설립된 보험요율산출기관이며,
					중고차사고이력정보서비스(www.carhistory.or.kr)는 보험업법시행령 제86조 제1호에 근거하여 제공합니다.
				</p>
				<span className={cx('date')}>{dayjs(accident.regDate).format('YYYY년 MM월 DD일')}</span>
				<p className={cx('stamp')}>보험개발원장</p>
			</div>
			<p className={cx('desc_notice')}>자동차보험사고기록이 없었다고 해서 반드시 무사고라고 할수는 없습니다.</p>
		</>
	);
}

export default ReportAccidentInfo;
